function formatDate(date: string) {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  const formattedDate = new Date(date)
    .toLocaleDateString('en-GB', options)
    .toUpperCase();
  return formattedDate.replace(/(\w{3}) (\d{4})$/, '$1, $2');
}

// Normalizes a Date or date string to an ISO string, or null if invalid/empty.
function toIso(date: Date | string | undefined): string | null {
  if (date === undefined || date === null) return null;
  const parsed = date instanceof Date ? date : new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

// Returns the most recent valid date of lastUpdated / lastReviewed (as ISO) with its label, or null.
function getSecondaryDate(
  lastUpdated: Date | string | undefined,
  lastReviewed: Date | string | undefined
): { label: 'Updated' | 'Reviewed'; date: string } | null {
  const updated = toIso(lastUpdated);
  const reviewed = toIso(lastReviewed);

  if (updated && reviewed) {
    return new Date(reviewed).getTime() > new Date(updated).getTime()
      ? { label: 'Reviewed', date: reviewed }
      : { label: 'Updated', date: updated };
  }
  if (updated) return { label: 'Updated', date: updated };
  if (reviewed) return { label: 'Reviewed', date: reviewed };
  return null;
}

type PostDateAndReadTimeProps = Readonly<{
  readTime: number | null | undefined;
  // Listing/card layouts: a single, unlabelled date.
  displayDate?: Date | string;
  // Article-page layout: labelled "Published" plus the latest of lastUpdated/lastReviewed.
  publishedAt?: Date | string;
  lastUpdated?: Date | string;
  lastReviewed?: Date | string;
}>;

export default function PostDateAndReadTime({
  readTime,
  displayDate,
  publishedAt,
  lastUpdated,
  lastReviewed,
}: PostDateAndReadTimeProps) {
  // Article-page mode: labelled published date + optional secondary date.
  if (publishedAt !== undefined) {
    const secondary = getSecondaryDate(lastUpdated, lastReviewed);
    const publishedIso = toIso(publishedAt);

    return (
      <div className='tracking flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] tracking-[1.7px]'>
        {publishedIso && (
          <span className='whitespace-nowrap font-medium text-[#737373]'>
            PUBLISHED:{' '}
            <time dateTime={publishedIso}>{formatDate(publishedIso)}</time>
          </span>
        )}
        {publishedIso && secondary && (
          <span aria-hidden className='font-medium text-[#737373]'>
            &bull;
          </span>
        )}
        {secondary && (
          <span className='whitespace-nowrap font-medium text-[#737373]'>
            {secondary.label.toUpperCase()}:{' '}
            <time dateTime={secondary.date}>{formatDate(secondary.date)}</time>
          </span>
        )}
        {readTime && <span>-</span>}
        {readTime && (
          <span className='whitespace-nowrap font-medium text-charcoal'>
            {`${readTime} MIN READ`}
          </span>
        )}
      </div>
    );
  }

  // Listing/card mode: single unlabelled date (existing behaviour).
  const displayIso = toIso(displayDate);
  return (
    <div className='tracking flex items-center space-x-2 text-[10px] tracking-[1.7px]'>
      {displayIso && (
        <span className='whitespace-nowrap font-medium text-[#737373]'>
          <time dateTime={displayIso}>{formatDate(displayIso)}</time>
        </span>
      )}
      {readTime && <span>-</span>}
      {readTime && (
        <span className='whitespace-nowrap font-medium text-charcoal'>
          {`${readTime} MIN READ`}
        </span>
      )}
    </div>
  );
}
